import { WindowLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useCheckToken } from '@/hooks/privacy/useCheckToken';
import { useLogout } from '@/hooks/privacy/useLogout';

import { env } from '../config/env.client';
import { paths } from '../constants/paths';
import { isPublicRoute } from '../helpers/routeManager';

const INACTIVITY_LIMIT = env.GATSBY_PUBLIC_INACTIVITY_LIMIT;
const DEBOUNCE_DELAY = 5000; // 5 segundos

interface AuthContextType {
  isLoggedIn: boolean;
  handleLogIn: () => void;
  isAuthentication: () => Promise<void>;
  handleLogout: () => Promise<void>;
}

const AuthContext = createContext({} as AuthContextType);

interface AuthContextProviderProps {
  children: React.ReactNode;
  location: WindowLocation | undefined;
}

export function AuthContextProvider({ children, location }: AuthContextProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { execute: checkToken } = useCheckToken();
  const { execute: logout } = useLogout();

  const handleLogIn = () => {
    setIsLoggedIn(true);
  };

  const isAuthentication = async () => {
    try {
      await checkToken();
      handleLogIn();
    } catch {
      throw Error('Unauthorization.');
    }
  };

  const handleLogout = useCallback(async () => {
    const currentPath = window.location.pathname;

    if (!isPublicRoute()) {
      sessionStorage.setItem('@Lacrei:redirect', currentPath);
    }
    setIsLoggedIn(false);
    try {
      await logout();
    } catch {
      navigate(paths.home);
    } finally {
      navigate(paths.home);
    }
  }, [logout, navigate]);

  // Ao entrar na aplicação é verificado a validade do
  // token direto no back-end.
  useEffect(() => {
    const checkAuth = async () => {
      if (isPublicRoute()) {
        return;
      }

      try {
        await isAuthentication();
      } catch {
        handleLogout();
      }
    };

    checkAuth();
  }, [location?.pathname, handleLogout]);

  // Realiza logout em casos de inatividade
  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(handleLogout, INACTIVITY_LIMIT);
    };

    const debouncedReset = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(resetTimer, DEBOUNCE_DELAY);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, debouncedReset));

    resetTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      events.forEach((event) => window.removeEventListener(event, debouncedReset));
    };
  }, [handleLogout]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        handleLogIn,
        handleLogout,
        isAuthentication,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
